import {
  DraftDataDto,
  EntityType,
  FileMetaDataDto,
  FormChildQuestionDto,
  FormQuestionDto,
  UploadedFileFeature,
} from "@green0meter/api-client";
import React, { FC, useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { isEmpty, sortBy } from "lodash";

import { Box, Button, Stack } from "@components/base";
import {
  useCreateUpdateDraftData,
  useDeleteDraftData,
  useGetFormDraftData,
  useUploadFile,
} from "@api";
import { useUserStore } from "@store/user";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import FileListItem from "@components/common/FileListItem";

interface Files {
  parentQuestionCode?: string;
  question: FormQuestionDto | FormChildQuestionDto;
  disabled?: boolean;
  rowIndex?: number;
  originalQuestionCode?: string;
  formId?: string;
}

const Files: FC<Files> = ({
  parentQuestionCode,
  question,
  disabled,
  rowIndex,
  originalQuestionCode,
  formId,
}) => {
  const [files, setFiles] = useState<DraftDataDto[]>([]);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    formSettings: { formBasics },
  } = useMultistepFormStore();
  const { user } = useUserStore();
  const { uploadFileAsync, uploadFileEnabled } = useUploadFile(user);
  const { createUpdateDraftDataAsync, createUpdateDraftDataEnabled } =
    useCreateUpdateDraftData(user, formBasics.id);
  const { formDraftData } = useGetFormDraftData(user, formBasics.id);

  const { deleteDraftData } = useDeleteDraftData(user, formBasics.id);

  const handleSelectFile = (): void => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDelete = async (draftData: DraftDataDto): Promise<void> => {
    deleteDraftData(draftData);
  };

  const handleUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files;

    if (files && createUpdateDraftDataEnabled) {
      const fileList = Array.from(files);

      for (const file of fileList) {
        const uploadedFile = await uploadFileAsync({
          file,
          entityId: formId ?? user?.activeCompanyId ?? "",
          databaseEntityType: EntityType.Form,
          uploadedFileFeature: UploadedFileFeature.FormData,
        });

        const questionCode =
          rowIndex && originalQuestionCode ? originalQuestionCode : question.code;

        await createUpdateDraftDataAsync({
          formId: formBasics.id,
          questionCode: questionCode,
          parentQuestionCode: parentQuestionCode,
          value: uploadedFile.fileId,
          rowIndex,
        });
      }
    }
  };

  useEffect(() => {
    const _files =
      formDraftData?.filter((d) => {
        if (isEmpty(d.value)) {
          return false;
        }

        if (rowIndex) {
          return d.questionCode === originalQuestionCode && d.rowIndex === rowIndex;
        }

        if (parentQuestionCode) {
          return (
            d.questionCode === question.code &&
            d.parentQuestionCode === parentQuestionCode
          );
        }

        return d.questionCode === question.code;
      }) || [];

    setFiles(sortBy(_files, ["valueUpdatedDateTime", "desc"]));
  }, [formDraftData, question.code, rowIndex, originalQuestionCode]);

  return (
    <Box>
      {files.length > 0 && (
        <Stack spacing={1} mb={2}>
          {files.map((draftData) => (
            <FileListItem
              key={draftData.id}
              file={draftData.value as FileMetaDataDto}
              onDelete={() => handleDelete(draftData)}
            />
          ))}
        </Stack>
      )}
      {uploadFileEnabled && (
        <>
          <input
            type="file"
            id={question.questionId}
            name={question.questionId}
            disabled={disabled}
            multiple
            hidden
            ref={inputRef}
            onChange={handleUpload}
          />
          <Button
            variant="text"
            startIcon={<FeatherIcon icon="paperclip" />}
            size="small"
            onClick={handleSelectFile}
          >
            {t("common.uploadYourFile")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default Files;
